import { Component, OnDestroy, OnInit } from '@angular/core';
import { DataSourceParams, ETableColumnType, ITableColumn } from '../../store/models/table.model';
import { IBlockedDate } from '../../store/models/campaigns.model';
import { BaseDataSource } from '../../datasources/base.datasource';
import { IUserSessionData, UserType } from '../../store/models/user.model';
import { ApiDataSource } from '../../datasources/api.datasrouce';
import { environment } from '../../../environments/environment';
import { Button, ButtonColor, ButtonSize } from '../../store/models/button.model';
import { Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { SessionService } from '../../store/services/session.service';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { IReducers } from '../../store/reducers';
import { openDialog, openSnackbar } from '../../store/actions/page.action';
import { CampaignService } from '../../store/services/campaign.service';

@Component({
  selector: 'app-blocked-dates-v2',
  templateUrl: './blocked-dates-v2.component.html',
  styleUrls: ['./blocked-dates-v2.component.scss'],
})
export class BlockedDatesV2Component implements OnInit, OnDestroy {

  insightsButtonProperties: Button = {
    text: 'Insights',
    color: ButtonColor.ORANGE,
    size: ButtonSize.NORMAL,
    onClick: () => this.store$.dispatch(openDialog({
      dialogData: {
        title: 'New Block Date System Notes',
        canClose: true,
        content: `<!DOCTYPE html>
<html>
<head>
  <title></title>
</head>
<body>
  <ul>
    <li>National holidays are configured at system level. Admin cannot delete or edit system level dates.</li>
    <li>Admin can create a date and apply rules per date.</li>
    <li>If only date from is passed - this will block only the selected date.</li>
    <li>If date from-to are passed - this will block the specified range of dates.</li>
    <li>If range of dates is passed - every single date within the range will appear as a unique.</li>
    <li>During creation of a block date, admin can define specific rules. Rules will be applied to all dates within the blocked range.</li>
    <li>Admin can later EDIT existing rules per date.</li>
    <li>The rules work on an "OR" principle.</li>
  </ul>

  <h2>Rules</h2>

  <h3>Block Email Types</h3>
  <p>Block the date for a specific email type:</p>
  <ul>
    <li>Campaign</li>
    <li>Follow up</li>
    <li>Cycle</li>
  </ul>

  <h3>TimeZones</h3>
  <ul>
    <li>Eastern Time (US & Canada)</li>
    <li>Alaska</li>
    <li>Hawaii</li>
    <li>Pacific Time (US & Canada)</li>
    <li>Mountain Time (US & Canada)</li>
    <li>Central Time (US & Canada)</li>
    <li>Atlantic Time (Canada)</li>
    <li>Newfoundland</li>
    <li>Dublin</li>
    <li>Atlantic/Stanley</li>
    <li>Atlantic/South_Georgia</li>
    <li>America/Anguilla</li>
    <li>Pacific/Pitcairn</li>
    <li>Europe/Amsterdam</li>
    <li>Europe/Athens</li>
    <li>America/Grand_Turk</li>
    <li>London</li>
    <li>Lisbon</li>
    <li>Madrid</li>
    <li>Portugal</li>
    <li>Perth</li>
    <li>Darwin</li>
    <li>Brisbane</li>
    <li>Adelaide</li>
    <li>Sydney</li>
    <li>Australia/Lord_Howe</li>
    <li>Australia/Eucla</li>
    <li>Asia/Dubai</li>
  </ul>

  <h3>States</h3>
  <ul>
    <li>AB, BC, MB, NB, NL, NT, NS, NU, ON, PE, QC, SK, YT, AL, AK, AZ, AR, CA, CO, CT, DE, DC, FL, GA, HI, ID, IL, IN, IA, KS, KY, LA, ME, MD, MA, MI, MN, MS, MO, MT, NE, NV, NH, NJ, NM, NY, NC, ND, OH, OK, OR, PA, PR, RI, SC, SD, TN, TX, UT, VT, VA, WA, WV, WI, WY</li>
    <li>Custom states: CI, Sheffield, West Sussex, NSW, VIC</li>
  </ul>

  <h3>Managers</h3>
  <p>Define which managers will be blocked for the date. All subordinates under the managers will be blocked based on the applied set of rules for the date.</p>

  <h3>Partners</h3>
  <p>Define which partners will be blocked for the date. All subordinates under the partners will be blocked based on the applied set of rules for the date.</p>

  <h3>Example 1:</h3>
  <p>2025/01/01 is blocked</p>
  <p>1st set of rules defines:</p>
  <ul>
    <li>Email types as "campaign, followup"</li>
    <li>States as: "al, ok"</li>
    <li>Managers as: 111</li>
  </ul>

  <p>2nd set of rules applied to the same date defines:</p>
  <ul>
    <li>Email types as "campaign"</li>
  </ul>

  <p>In Example 1, all campaigns for all users will be blocked.</p>
  <p>Also follow ups will be blocked for users that are in states AL, OK, or are assigned under manager id 111.</p>

  <h3>Example 2:</h3>
  <p>2025/01/02 to 2025/01/03 is blocked</p>
  <p>With rules:</p>
  <p>1st set of rules:</p>
  <ul>
    <li>Timezone: EST</li>
    <li>Emails: "cycle", "followup"</li>
    <li>Partner: 222</li>
  </ul>

  <p>In example 2, all cycles and follow ups will be blocked if user is in timezone EST, OR user is assigned under partner id 222.</p>

</body>
</html>`,
      },
    })),
  };

  createNewButtonProperties: Button = {
    text: 'New',
    color: ButtonColor.GREEN,
    size: ButtonSize.NORMAL,
    route: ['/campaigns', 'blocked_dates', 'edit'],
  };
  dataSource: BaseDataSource<IBlockedDate>;
  dataSourceParams: DataSourceParams = {
    page: 1,
    pageSize: 10,
    sortDir: 'asc',
    sortAttr: 'block_date',
    filters: {},
  };
  sessionData: IUserSessionData;
  tableColumns: ITableColumn<IBlockedDate>[] = [];
  componentReady = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly httpClient: HttpClient,
    private readonly sessionService: SessionService,
    private readonly store$: Store<IReducers>,
    private readonly campaignsService: CampaignService,
  ) {
  }

  ngOnInit(): void {
    this.sessionService.onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe(session => {
        this.sessionData = session;
        this.dataSource = new ApiDataSource(this.httpClient, environment.apiUrl + '/v1/block_dates');
        if (this.sessionData.user_type !== UserType.MASTER) {
          this.tableColumns = [
            {
              id: 'block_date',
              title: 'Date',
              isDateColumn: true,
              dateFormat: 'MM/DD/YYYY',
            },
            {
              id: 'blocked_emails',
              title: 'Blocked Emails',
            },
            {
              id: 'delete',
              title: 'Delete',
              type: ETableColumnType.BUTTON,
              getButtonProperties: (element: IBlockedDate) => ({
                color: ButtonColor.RED,
                text: 'Delete',
                size: ButtonSize.NORMAL,
                onClick: (data: IBlockedDate) => this.deleteDate(data),
                buttonDisabled: (data: IBlockedDate) => data.account_id === null && data.account_id !== this.sessionData.id,
              }),
            },
          ];
        } else {
          this.tableColumns = [{
            id: 'block_date',
            title: 'Date',
            isDateColumn: true,
            dateFormat: 'MM/DD/YYYY',
          },
            {
              id: 'blockedRules',
              title: 'Rules',
              renderer: row => {
                if (!row.blockedRules) {
                  return 'Holiday';
                }

                return `${row.blockedRules?.length} rules`;
              },
            },
            {
              id: 'edit',
              title: 'Edit',
              type: ETableColumnType.BUTTON,
              getButtonProperties: (element: IBlockedDate) => ({
                text: 'Edit',
                color: ButtonColor.ORANGE,
                size: ButtonSize.NORMAL,
                route: ['/campaigns', 'blocked_dates', 'edit', element.id],
                buttonDisabled: (data: IBlockedDate) => data.blocked_by !== 'Admin',
              }),
            },
            {
              id: 'delete',
              title: 'Delete',
              type: ETableColumnType.BUTTON,
              getButtonProperties: (element: IBlockedDate) => ({
                color: ButtonColor.RED,
                text: 'Delete',
                size: ButtonSize.NORMAL,
                onClick: (data: IBlockedDate) => this.deleteDate(data),
                buttonDisabled: (data: IBlockedDate) => data.blocked_by !== 'Admin',
              }),
            },
          ];
        }
        this.tableColumns.push();
        this.componentReady = true;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  private deleteDate(blockDate: IBlockedDate) {
    this.store$.dispatch(openDialog({
      dialogData: {
        content: 'Are you sure you want to delete this date?',
        title: `Delete date ${blockDate.block_date}`,
        canClose: true,
        closeButtonText: 'No',
        buttons: [
          {
            text: 'Yes',
            color: ButtonColor.RED,
            size: ButtonSize.NORMAL,
            onClick: (dialog) => {
              this.campaignsService.deleteBlockDate(blockDate.id)
                .subscribe(() => {
                  this.store$.dispatch(openSnackbar({
                    snackbarData: {
                      data: {
                        message: `Block date ${blockDate.block_date} was successfully deleted!`,
                        type: 'success',
                      },
                    },
                  }));
                  dialog.closeDialog();
                  this.dataSource.refresh();
                }, err => {
                  this.store$.dispatch(openSnackbar({
                    snackbarData: {
                      data: {
                        message: err.error,
                        type: 'error',
                      },
                    },
                  }));
                  dialog.closeDialog();
                });
            },
          },
        ],
      },
    }));
  }
}
